import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Eye, X } from "lucide-react";

/**
 * ImpersonationBanner — Phase 7.32.
 *
 * Banner amber exibido no topo do AppLayout do cliente quando há uma
 * sessão de impersonação ativa (admin entrou no painel via "Impersonar").
 *
 * Fonte de verdade: `localStorage.imp_session` — populado pelo Admin
 * (`/admin/workspaces`) ao clicar em "Impersonar". Compartilhado entre
 * abas do mesmo domínio.
 *
 * Modo FULL ACCESS — o admin navega como se fosse o proprietário, com
 * todos os botões funcionais (sem read-only enforcement).
 */
export default function ImpersonationBanner() {
  const [session, setSession] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Hidrata o localStorage a partir do query param ?imp=... na primeira visita
  // (caso o admin tenha aberto a nova aba diretamente via target_url do backend).
  //
  // Jun/2026 — `ws` (workspace_id) passou a ser gravado. Sem ele,
  // `impHeaders()` não enviava `X-Imp-Workspace` e TODAS as rotas S360
  // respondiam 400 ("Impossível resolver empresa_id") — o V360 abria a
  // empresa certa e o Sprints360 aparecia sem nenhum projeto.
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const impToken = sp.get("imp");
    const empresaId = sp.get("empresa");
    const wsId = sp.get("ws");
    if (impToken && empresaId) {
      try {
        const existing = JSON.parse(localStorage.getItem("imp_session") || "null");
        const wsResolvido = wsId
          || (existing?.empresa_perfil_id === empresaId ? existing?.workspace_id : null)
          || null;
        if (!existing || existing.token !== impToken || (wsResolvido && !existing.workspace_id)) {
          // Marca a sessão de impersonação. Nome será resolvido pelo TenantContext
          // ao carregar o perfil da empresa.
          localStorage.setItem("imp_session", JSON.stringify({
            ...(existing || {}),
            token: impToken,
            empresa_perfil_id: empresaId,
            workspace_id: wsResolvido,
            workspace_nome: existing?.workspace_nome || null,
            full_access: true,
            started_at: new Date().toISOString(),
          }));
          window.dispatchEvent(new CustomEvent("imp-session-changed"));
        }
      } catch { /* noop */ }
    }
  }, [location.search]);

  useEffect(() => {
    const read = () => {
      try {
        const raw = localStorage.getItem("imp_session");
        setSession(raw ? JSON.parse(raw) : null);
      } catch {
        setSession(null);
      }
    };
    read();
    window.addEventListener("imp-session-changed", read);
    window.addEventListener("storage", read);
    return () => {
      window.removeEventListener("imp-session-changed", read);
      window.removeEventListener("storage", read);
    };
  }, []);

  if (!session) return null;

  const handleEnd = () => {
    localStorage.removeItem("imp_session");
    window.dispatchEvent(new CustomEvent("imp-session-changed"));
    // Volta para a tela de Workspaces (tenants) no admin.
    navigate("/admin/workspaces");
  };

  const nomeEmpresa = session.workspace_nome || "empresa selecionada";

  return (
    <div
      className="bg-amber-100 border-y border-amber-300 text-amber-900 text-[11px] sm:text-xs px-3 sm:px-5 py-2 flex flex-wrap items-center justify-center gap-2 font-medium"
      data-testid="app-impersonation-banner"
    >
      <Eye className="w-3.5 h-3.5 shrink-0" />
      <span className="text-center">
        <strong>Modo Espelhamento (Ghost)</strong> · Você está visualizando <strong>{nomeEmpresa}</strong> como proprietário · acesso pleno
      </span>
      <button
        onClick={handleEnd}
        className="ml-2 px-2.5 py-1 rounded bg-amber-900 text-white text-[11px] shrink-0 hover:bg-amber-800 inline-flex items-center gap-1.5"
        data-testid="app-impersonation-end"
      >
        <X className="w-3 h-3" /> Sair do Modo Ghost
      </button>
    </div>
  );
}
